import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { RateLimitedError as ApiRateLimitedError, SpamDetectedError } from "../../lib/api";
import { CONNECTOR_LABEL, type ConnectorType } from "../stations/connectors";
import { TurnstileChallenge } from "../reports/TurnstileChallenge";

import type { ConnectorRequest } from "./api";
import { RateLimitedError, useSubmitStationRequest } from "./hooks";

const CONNECTOR_TYPES: ConnectorType[] = [
  "type2",
  "ccs",
  "chademo",
  "tesla",
  "type1",
  "gbt",
  "nacs",
];

type ConnectorRow = ConnectorRequest & { id: number };

type Outcome =
  | { kind: "idle" }
  | { kind: "success" }
  | { kind: "duplicate" }
  | { kind: "error"; message: string };

type Props = {
  onSuccess?: () => void;
};

export function RequestStation({ onSuccess }: Props) {
  const { t, i18n } = useTranslation();
  const submit = useSubmitStationRequest();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [connectors, setConnectors] = useState<ConnectorRow[]>([]);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });
  const nextId = useRef(0);

  const canSubmit = name.trim().length > 0 && !!turnstileToken && !submit.isPending;

  function addConnector() {
    setConnectors((prev) => [
      ...prev,
      { id: nextId.current++, connector_type: "ccs", power_kw: 50 },
    ]);
  }

  function updateConnector(id: number, patch: Partial<ConnectorRequest>) {
    setConnectors((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }

  function removeConnector(id: number) {
    setConnectors((prev) => prev.filter((c) => c.id !== id));
  }

  async function onSubmit() {
    setOutcome({ kind: "idle" });
    try {
      await submit.mutateAsync({
        name: name.trim(),
        address: address.trim() || undefined,
        connectors: connectors.map(({ connector_type, power_kw }) => ({ connector_type, power_kw })),
        locale: i18n.language,
        cfTurnstileResponse: turnstileToken ?? undefined,
      });
      setName("");
      setAddress("");
      setConnectors([]);
      setOutcome({ kind: "success" });
      onSuccess?.();
    } catch (err) {
      if (err instanceof RateLimitedError || err instanceof ApiRateLimitedError) {
        setOutcome({ kind: "duplicate" });
        return;
      }
      if (err instanceof SpamDetectedError) {
        // Turnstile token is one-time-use — reset so the user gets a fresh challenge.
        setTurnstileToken(null);
        setOutcome({ kind: "error", message: t("stations.request.error_captcha") });
        return;
      }
      console.error("[stationRequest] submit failed", err);
      setOutcome({ kind: "error", message: t("stations.request.error_generic") });
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
      className="space-y-4"
    >
      <fieldset>
        <label className="block text-sm font-semibold text-fg-mute" htmlFor="req-name">
          {t("stations.request.name_label")}
        </label>
        <input
          id="req-name"
          required
          minLength={1}
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm"
        />
      </fieldset>

      <fieldset>
        <label className="block text-sm font-semibold text-fg-mute" htmlFor="req-address">
          {t("stations.request.address_label")}
        </label>
        <input
          id="req-address"
          maxLength={300}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm"
        />
      </fieldset>

      <fieldset className="space-y-2">
        <span className="block text-sm font-semibold text-fg-mute">
          {t("stations.request.connectors_label")}
        </span>
        {connectors.map((c) => (
          <div key={c.id} className="flex items-center gap-2">
            <select
              aria-label={t("stations.request.connector_type_label")}
              value={c.connector_type}
              onChange={(e) =>
                updateConnector(c.id, { connector_type: e.target.value as ConnectorType })
              }
              className="rounded-md border border-line px-2 py-2 text-sm"
            >
              {CONNECTOR_TYPES.map((type) => (
                <option key={type} value={type}>
                  {CONNECTOR_LABEL[type]}
                </option>
              ))}
            </select>
            <input
              type="number"
              aria-label={t("stations.request.connector_power_label")}
              min={1}
              max={400}
              value={c.power_kw}
              onChange={(e) => {
                const v = e.target.valueAsNumber;
                if (!Number.isNaN(v)) updateConnector(c.id, { power_kw: v });
              }}
              className="num w-20 rounded-md border border-line px-2 py-2 text-sm"
            />
            <span className="text-xs text-fg-mute">kW</span>
            <button
              type="button"
              onClick={() => removeConnector(c.id)}
              aria-label={t("stations.request.connector_remove")}
              className="ml-auto text-xs font-medium text-fg-mute hover:text-ember"
            >
              {t("stations.request.connector_remove")}
            </button>
          </div>
        ))}
        {connectors.length < 8 && (
          <button
            type="button"
            onClick={addConnector}
            className="flex items-center gap-1.5 rounded-md border border-dashed border-line-strong px-3 py-2 text-sm font-medium text-fg-mute hover:border-volt/60 hover:text-fg"
          >
            <span aria-hidden="true">＋</span>
            {t("stations.request.connector_add")}
          </button>
        )}
      </fieldset>

      <TurnstileChallenge onVerify={setTurnstileToken} />

      <OutcomeBanner outcome={outcome} />

      <button
        type="submit"
        disabled={!canSubmit}
        className="btn-volt inline-flex items-center justify-center px-5 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submit.isPending ? t("stations.request.submit_sending") : t("stations.request.submit")}
      </button>
    </form>
  );
}

function OutcomeBanner({ outcome }: { outcome: Outcome }) {
  const { t } = useTranslation();
  if (outcome.kind === "success") {
    return (
      <p role="status" className="text-sm text-volt-deep">
        {t("stations.request.success")}
      </p>
    );
  }
  if (outcome.kind === "duplicate") {
    return (
      <p role="status" className="text-sm text-fg-mute">
        {t("stations.request.duplicate")}
      </p>
    );
  }
  if (outcome.kind === "error") {
    return (
      <p role="alert" className="text-sm text-ember">
        {outcome.message}
      </p>
    );
  }
  return null;
}
